import { useCallback, useEffect, useMemo, useState } from "react";

import { IVehicle } from "@/types/vehicle.type";
import { getIn, useFormikContext } from "formik";

import SelectFormik from "@/common/components/form/SelectFormik";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import RegisterPhotos from "@/modules/renters/components/modals/fields/EditBondRenterVehcile/formsModal/field/RegisterPhotos";
import InputFormik from "@/common/components/form/InputFormik";
import { IRenter } from "@/types/renter.type";
import { collectorResponse, getCollectorByDocument, getRenterByLicensePlate, getVehicleList, sendRenterPaymentProof, sendRenterSelfieWithAddress } from "@/common/services/pre-collection.api";
import { HiSearch } from "react-icons/hi";
import GenericInput from "@/common/components/form/GenericInput";
import { UploadDocumentField } from "@/modules/renters/components/forms/fields/UploadDocumentField";
import { UploadImageField } from "@/common/components/form/UploadImageField/UploadImageField";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { maskCPF } from "@/common/helpers/mask.helpers";
import { GenericCheckboxField } from "@/common/components/GenericCheckboxField";

type CollectorLocation = {
  mapsUrl: string;
  latitude: number;
  longitude: number;
  currentTime: Date;
}

type InspectionFormValues = {
  vehicleId: string;
  renterId: string;
  collectorDocument: string;
  debitPaid: boolean
  selectedLicensePlate: string;
  selfieWithAddress: string;
  paymentProof: string;
  registers: Array<{ type: string; content: string; verified?: boolean }>;
  optionRepossesion: string;
};

type Props = {
  submitButtonText: string;
  isLoading: boolean;
};

export default function VehicleInspectionFormBody({ submitButtonText, isLoading }: Props) {
  const { notifyInfo, notifyError } = useToastContext();

  const formik = useFormikContext<InspectionFormValues>();
  const { values, setFieldValue } = formik;

  const registers = getIn(formik.values, "registers");
  const returnReason = getIn(formik.values, "returnReason");

  const [vehicles, setVehicles] = useState<IVehicle[]>([]);
  const [collector, setCollector] = useState<IRenter | null>(null);
  const [disableButton, setDisableButton] = useState(true);
  const [collectorLocation, setCollectorLocation] = useState<CollectorLocation | null>(null);
  const [selfieWithAddressSented, setSelfieWithAddressSent] = useState(false);

  const pathToUpload = useMemo(() => `pre-collection/${collector?._id}`, [collector?._id]);
  const pathToUploadPaymentProof = useMemo(() => `pre-collection/payment-proof/${collector?._id}`, [collector?._id]);

  useEffect(() => {
    if (returnReason !== "" || values.debitPaid) {
      formik.setFieldValue("optionRepossesion", "DEFAULT")

      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnReason, values.debitPaid]);

  useEffect(() => {
    if (values.debitPaid) {
      formik.setFieldValue("optionRepossesion", "DEFAULT")
      formik.setFieldValue("returnReason", "Débito pago pelo cliente.")
    } else {
      formik.setFieldValue("optionRepossesion", "")
      formik.setFieldValue("returnReason", "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.debitPaid]);

  const onAddRegister = () => {
    formik.setFieldValue("registers", [
      ...registers,
      { type: "", content: "", verified: false },
    ]);
  };

  const handleVehicleChange = useCallback(async (e: React.ChangeEvent<HTMLSelectElement> | { target: { value: string; } }) => {
    const selectedPlate = e.target.value;
    const currentPlate = values.selectedLicensePlate;

    if (!selectedPlate || selectedPlate === "") {
      setFieldValue("selectedLicensePlate", "");
      setFieldValue("vehicleId", "");
      setFieldValue("registers", []);
      return;
    }

    if (selectedPlate !== currentPlate) {
      setFieldValue("selectedLicensePlate", selectedPlate);

      const selectedVehicle = vehicles.find(
        (v) => v.licensePlate === selectedPlate
      );

      if (selectedVehicle) {
        setFieldValue("vehicleId", selectedVehicle.vehicleId);

        // Reset dos registros
        const initialRegisters = [
          { type: "Frente do veículo", content: "" },
          { type: "Lateral direita do veículo", content: "" },
          { type: "Lateral esquerda do veículo", content: "" },
          { type: "Traseira do veículo", content: "" },
          { type: "Odômetro", content: "" },
        ];
        setFieldValue("registers", initialRegisters);
      }
    }

    setSelfieWithAddressSent(false);

    try {
      const response = await getRenterByLicensePlate(selectedPlate);

      if (response.ok && response.data) {
        setFieldValue("renterId", response.data._id);
      } else {
        setFieldValue("renterId", "");
        notifyError("Não foi possível encontrar o cadastro do renter para a placa selecionada.");
      }
    } catch (error) {
      console.error("Erro ao buscar renter pela placa do veículo:", error);
      notifyError("Erro ao buscar informações do renter. Tente novamente.");
    }
  }, [values.selectedLicensePlate, setFieldValue, vehicles, notifyError]);

  const handleGetCollector = async (documentCollector?: string) => {

    const inputDocument = documentCollector ? null : document.getElementById("collectorDocument") as HTMLInputElement;
    const documentValue = documentCollector || inputDocument?.value || "";

    if (documentValue.length < 14) return

    try {
      if (isLoading) {
        notifyInfo("Aguarde o término do carregamento atual.");
        return;
      }

      const response = await getCollectorByDocument(documentValue);

      if (response.ok && response.data) {
        if (!response.data) return

        setCollector(response.data);
        setFieldValue("collectorDocument", response.data.cpf);

        const collectorData: collectorResponse = {
          companyId: response.data.companyId,
          collectorId: response.data._id!,
        }
        const vehicleListResponse = await getVehicleList(collectorData)

        if (!(vehicleListResponse.data.length > 0)) {
          notifyError("Não foi possível buscar a lista de veículos para o recolhedor.");
          setVehicles([]);
          return;
        }
        setVehicles(vehicleListResponse.data);
      } else {
        setCollector(null);
        notifyError("Não foi possível encontrar o cadastro do recolhedor.");
      }
    } catch (error) {
      console.error("Erro ao buscar cliente:", error);
      setCollector(null);
    }
  }

  const handleRenterSelfieUpload = useCallback(async (src: string) => {
    try {
      if (!values.collectorDocument) return;

      if (!collectorLocation) {
        notifyError("Localização do coletor não disponível. Não é possível enviar a selfie com endereço.");
        return;
      }

      setFieldValue("selfieWithAddress", src);
      setSelfieWithAddressSent(true);

      const response = await sendRenterSelfieWithAddress(values.renterId || "", {
        vehicleId: values.vehicleId,
        licensePlate: values.selectedLicensePlate,

        collector: {
          name: collector?.name || "",
          document: collector?.cpf || "",
          selfieWithAddress: src,
          location: collectorLocation
        }
      });

      if (response.ok) {
        setSelfieWithAddressSent(true);
      } else {
        setSelfieWithAddressSent(false);
        setFieldValue("selfieWithAddress", "");
        const { message } = response.data

        notifyInfo(message || "Falha ao enviar selfie com endereço. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao enviar selfie com endereço:", error);
      setSelfieWithAddressSent(false);
      setFieldValue("selfieWithAddress", "");
    }


  }, [values.collectorDocument, values.renterId, values.vehicleId, values.selectedLicensePlate, collectorLocation, setFieldValue, collector?.name, collector?.cpf, notifyError, notifyInfo]);

  const handleRenterPaymentProofUpload = useCallback(async (src: string) => {
    try {
      if (!values.collectorDocument) {
        notifyError("Documento do coletor não informado. Não é possível enviar o comprovante de pagamento.");
        return;
      }

      if (!collector) {
        notifyError("Dados do coletor não disponíveis. Não é possível enviar o comprovante de pagamento.");
        return;
      }

      setFieldValue("paymentProof", src);

      const response = await sendRenterPaymentProof(values.renterId, {
        vehicleId: values.vehicleId,
        debitPaid: values.debitPaid,
        collectorId: collector?._id || "",
        paymentProof: src
      })

      if (response.ok) {
        notifyInfo("Comprovante de pagamento enviado com sucesso.");
      } else {
        setFieldValue("paymentProof", "");
        const { message } = response.data

        notifyInfo(message || "Falha ao enviar comprovante de pagamento. Tente novamente.");
      }
    } catch (error) {
      notifyError("Erro ao enviar comprovante de pagamento. Tente novamente.");
    }
  }, [collector, notifyError, notifyInfo, setFieldValue, values.debitPaid, values.collectorDocument, values.renterId, values.vehicleId]);

  const registerPhotosComponent = useMemo(() => {
    // Se não há placa selecionada, retorna versão desabilitada
    if (!values.selectedLicensePlate && !selfieWithAddressSented) {
      return (
        <div className="opacity-50 pointer-events-none">
          <RegisterPhotos
            key="empty" // Key diferente para forçar remount
            title={"Inspeção de Veículo"}
            items={[]}
            onAddItem={onAddRegister}
            onRemoveItem={onAddRegister}
            titleTextArea={"Descreva o motivo da devolução:"}
            renterId={""}
            nameRenter={""}
            vehicleId={""}
            optionRepossesion={false}
          />
        </div>
      );
    }

    if (values.debitPaid) {
      return <></>
    }

    // Componente ativo com dados da placa selecionada
    return (
      <RegisterPhotos
        key={values.selectedLicensePlate} // Key baseada na placa para forçar remount
        title={"Inspeção de Veículo"}
        items={registers}
        onAddItem={onAddRegister}
        onRemoveItem={onAddRegister}
        titleTextArea={"Descreva o motivo da devolução:"}
        renterId={""}
        nameRenter={""}
        vehicleId={values.vehicleId}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.selectedLicensePlate, values.vehicleId, selfieWithAddressSented, values.debitPaid]);

  const handleGetCollectorLocation = useCallback(() => {
    if (collectorLocation) return;

    let storedPermission = localStorage.getItem("preCollectionAllowLocation");

    if (storedPermission === "false" || storedPermission === null) {
      if (confirm("Permitir que o sistema acesse sua localização para registrar o local do recolhimento?")) {
        localStorage.setItem("preCollectionAllowLocation", "true");
        storedPermission = "true";
      }

      return;
    }


    if (storedPermission === "true") {
      localStorage.setItem("preCollectionAllowLocation", "true");

      if (!navigator.geolocation) {
        notifyInfo("Geolocalização não é suportada pelo seu navegador.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords

          const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;


          setCollectorLocation({ mapsUrl, latitude, longitude, currentTime: new Date() });
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              notifyError("Permissão negada para acessar a localização.");
              break;
            case error.POSITION_UNAVAILABLE:
              notifyError("Informações de localização indisponíveis.");
              break;
            case error.TIMEOUT:
              notifyError("Tempo esgotado ao tentar obter localização.");
              break;
            default:
              notifyError("Erro desconhecido ao obter localização.");
              break;
          }
        }
      )

      return
    }

    notifyError("Não é possível realizar o recolhimento sem informar a sua localização.");
  }, [collectorLocation, notifyError, notifyInfo])

  useEffect(() => { handleGetCollectorLocation(); }, [handleGetCollectorLocation])

  return (
    <section>
      <div className="w-full flex items-end gap-3 mb-4">
        <InputFormik
          name="collectorDocument"
          label="Informe o CPF do recolhedor:"
          placeholder="000.000.000-00"
          mask={maskCPF}
          className="w-full"
        />

        <ButtonWithLoader type="button" onClick={() => handleGetCollector(values.collectorDocument)} >
          <HiSearch className="search-icon" />
        </ButtonWithLoader>
      </div>

      {collector && (
        <div className="mb-4">
          <GenericInput
            label={"Nome do recolhedor"}
            value={collector?.name}
            mask={(value: string) => value}
          />
        </div>
      )}

      {collector && collector._id && (
        <div className="mb-4">
          <SelectFormik
            label="Placa do Veículo:"
            name="selectedLicensePlate"
            options={vehicles.map(vehicle => vehicle.licensePlate)}
            onChange={handleVehicleChange}
            allowFilter={true}
            placeholder="Digite a placa para filtrar..."
          />
        </div>
      )}

      {values.selectedLicensePlate && values.collectorDocument && (
        <div className="mb-4">
          <label className="font-semibold text-sm">Selfie juntamente com o endereço:</label>

          <UploadDocumentField
            currentSrc={values.selfieWithAddress}
            pathToUpload={pathToUpload}
            uploadName="selfie-com-endereco"
            handleUploadSrc={handleRenterSelfieUpload}
          >
            {({ src, onNewFile, onRemoveClick }) => (
              <UploadImageField
                src={src}
                onNewFile={onNewFile}
                onRemoveClick={onRemoveClick}
              />
            )}
          </UploadDocumentField>
        </div>
      )}

      {values.selectedLicensePlate && values.collectorDocument && selfieWithAddressSented && (
        <GenericCheckboxField
          key={"debitPaid"}
          name={"debitPaid"}
          label={"Cliente efetuou o pagamento do débito?"}
          labels={{
            confirmed: "Pago",
            notConfirmed: "Não Pago"
          }}
          className="mb-4"
          onChange={() => { }}
        />
      )}

      {values.debitPaid && (
        <div className="mb-4">
          <label className="font-semibold text-sm">Foto do comprovante de pagamento:</label>

          <UploadDocumentField
            currentSrc={values.paymentProof}
            pathToUpload={pathToUploadPaymentProof}
            uploadName="comprovante-de-pagamento"
            handleUploadSrc={handleRenterPaymentProofUpload}
          >
            {({ src, onNewFile, onRemoveClick }) => (
              <UploadImageField
                src={src}
                onNewFile={onNewFile}
                onRemoveClick={onRemoveClick}
              />
            )}
          </UploadDocumentField>
        </div>
      )}

      <div className="h-3" />

      {selfieWithAddressSented && registerPhotosComponent}

      <div className="mt-8">
        <div className="flex justify-end mt-2">
          <ButtonWithLoader
            disabled={disableButton}
            type="submit"
            isLoading={isLoading}
          >
            {submitButtonText}
          </ButtonWithLoader>
        </div>
      </div>
    </section>
  );
}
